// Actions
import { ActionType } from "@ms/planner-actions/lib/local/ActionType";
import { DefaultPlanId, FlaggedEmailPlanId, StoreTableNames, TableName } from "@ms/uno-constants/lib/local/StoreConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
import { MyPlansPagePivotType } from "@ms/uno-constants/lib/local/ViewPivots";
import { applyFilter, registerIndex } from "@ms/uno-entitycore/lib/local/table/EntityTable";
import { Table } from "@ms/uno-entitycore/lib/local/table/Table";
// Models
import { OperationStatus } from "@ms/planner-models/lib/local/client/operations/Enumerations";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { PlanMoveOperationStatus } from "@ms/planner-models/lib/local/client/planner/Enumerations";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { EntitySlice } from "../../slice/EntitySlice";
import { Store } from "../Store";
// Utilities
import { isEntityAction } from "@ms/planner-actions/lib/local/utils/ActionUtils";
import { toViewIdString } from "@ms/uno-utilities/lib/local/ViewUtilities";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { filterPersistedTablesForStore } from "../../utilities/StoreUtilities";
// Plan store indexes
var PlanIndex = /*#__PURE__*/ function(PlanIndex) {
    PlanIndex["ContainerType"] = "containerType";
    PlanIndex["ContainerId"] = "containerId";
    PlanIndex["View"] = "view";
    return PlanIndex;
}(PlanIndex || {});
// Recommended plan set constants
const RecommendedPlanSetId = "recommended";
// View partition keys
const MyPlansViewPartition = "MyPlansView";
const MyPlansViewPivotIndex = "myPlansViewPivot";
export class PlanStore extends EventPublishingStore {
    get name() {
        return Store.Plan;
    }
    getPersistedTables() {
        return filterPersistedTablesForStore(Store.Plan, {
            [TableName.Plan]: this.planSlice.table(),
            ...this.planSlice.statusTables()
        });
    }
    getPlan(id, readBaseline) {
        return this.planSlice.get(id, readBaseline);
    }
    getPlansForContainer(containerType, containerId) {
        return this.planSlice.getCollection({
            name: "containerId",
            value: toContainerIdString(containerType, containerId)
        });
    }
    getPlansForContainerType(containerType) {
        return this.planSlice.getCollection({
            name: "containerType",
            value: containerType
        });
    }
    getPlanMoveOperationStatus(planId) {
        return this.movePlanOperationStatusTable.get(planId);
    }
    getCreatePlanFromTemplateOperationStatus(planId) {
        return this.createPlanFromTemplateOperationStatusTable.get(planId);
    }
    getDefaultPlanId() {
        return this.specialPlansTable.get(DefaultPlanId)?.[0] ?? null;
    }
    getFlaggedEmailPlanId() {
        return this.specialPlansTable.get(FlaggedEmailPlanId)?.[0] ?? null;
    }
    getRecommendedPlanIds() {
        return this.specialPlansTable.get(RecommendedPlanSetId) ?? [];
    }
    getPlansForView(viewId) {
        switch(viewId.name){
            case ViewName.MyPlansPage:
                return this.getMyPlansViewPartition().index(MyPlansViewPivotIndex).partition(viewId.partitionKey).items().map((item)=>item.value);
            default:
                throw new Error(`PlanPartitionNotFound [View=${toViewIdString(viewId)}]`);
        }
    }
    handleAction(action) {
        let storeChanged = false;
        if (!this.shouldHandleAction(action)) {
            return;
        }
        switch(action.type){
            case ActionType.FetchEntityAction:
                storeChanged = this.planSlice.handleFetchStarted(action.entityId);
                break;
            case ActionType.FetchEntityFailedAction:
                storeChanged = this.planSlice.handleFetchFailed(action.entityId, action.response);
                break;
            case ActionType.FetchEntitySucceededAction:
                storeChanged = this.planSlice.handleFetchSucceeded(action.entity.id, action.entity);
                break;
            case ActionType.FetchEntityCollectionAction:
                if (Object.values(ContainerType).includes(action.parentType)) {
                    storeChanged = this.planSlice.handleCollectionFetchStarted({
                        name: "containerId",
                        value: toContainerIdString(action.parentType, action.parentId)
                    });
                }
                break;
            case ActionType.FetchEntityCollectionFailedAction:
                if (Object.values(ContainerType).includes(action.parentType)) {
                    storeChanged = this.planSlice.handleCollectionFetchFailed({
                        name: "containerId",
                        value: toContainerIdString(action.parentType, action.parentId)
                    }, action.response);
                }
                break;
            case ActionType.FetchEntityCollectionSucceededAction:
                if (Object.values(ContainerType).includes(action.parentType)) {
                    storeChanged = this.planSlice.handleCollectionFetchSucceeded({
                        name: "containerId",
                        value: toContainerIdString(action.parentType, action.parentId)
                    }, true, Object.fromEntries(action.entities.map((item)=>[
                            item.id,
                            item
                        ])), [], action.deltaLink);
                }
                break;
            case ActionType.FetchAllPlansForContainerTypeAction:
                storeChanged = this.planSlice.handleCollectionFetchStarted({
                    name: "containerType",
                    value: action.containerType
                });
                break;
            case ActionType.FetchAllPlansForContainerTypeFailedAction:
                storeChanged = this.planSlice.handleCollectionFetchFailed({
                    name: "containerType",
                    value: action.containerType
                }, action.response);
                break;
            case ActionType.FetchAllPlansForContainerTypeSucceededAction:
                const storePlansArray = this.planSlice.getCollection({
                    name: "containerType",
                    value: action.containerType
                }).data;
                storeChanged = this.planSlice.handleCollectionFetchSucceeded({
                    name: "containerType",
                    value: action.containerType
                }, true, Object.fromEntries(storePlansArray.map((item)=>[
                        item.id,
                        item
                    ])), []);
                break;
            case ActionType.CreateEntityAction:
                storeChanged = this.planSlice.handleCreateStarted(action.entity.id, action.entity);
                break;
            case ActionType.CreateEntityFailedAction:
                storeChanged = this.planSlice.handleCreateFailed(action.entity.id);
                break;
            case ActionType.CreateEntitySucceededAction:
                storeChanged = this.planSlice.handleCreateSucceeded(action.localId, action.entity, action.entity.id);
                break;
            case ActionType.DeleteEntityAction:
                storeChanged = this.planSlice.handleDeleteStarted(action.entityId);
                break;
            case ActionType.DeleteEntityFailedAction:
                storeChanged = this.planSlice.handleDeleteFailed(action.entityId, action.response);
                break;
            case ActionType.DeleteEntitySucceededAction:
                storeChanged = this.planSlice.handleDeleteSucceeded(action.entityId);
                break;
            case ActionType.UpdateEntityAction:
                storeChanged = this.planSlice.handleUpdateStarted(action.entityId, action.updateKey, action.delta);
                break;
            case ActionType.UpdateEntityFailedAction:
                storeChanged = this.planSlice.handleUpdateFailed(action.entityId, action.updateKey, action.response);
                break;
            case ActionType.UpdateEntitySucceededAction:
                storeChanged = this.planSlice.handleUpdateSucceeded(action.entity.id, action.updateKey, action.entity);
                break;
            case ActionType.SetDefaultPlanAction:
                storeChanged = this.setSpecialPlanIds(DefaultPlanId, [
                    action.value
                ]);
                break;
            case ActionType.SetFlaggedEmailPlanAction:
                storeChanged = this.setSpecialPlanIds(FlaggedEmailPlanId, [
                    action.value
                ]);
                break;
            case ActionType.MovePlanOperationAction:
                storeChanged = this.handleMovePlanOperationAction(action);
                break;
            case ActionType.MovePlanOperationCompletedAction:
                storeChanged = this.handleMovePlanOperationCompletedAction(action);
                break;
            case ActionType.MovePlanOperationCompletedWithErrorsAction:
                storeChanged = this.handleMovePlanOperationCompletedWithErrorsAction(action);
                break;
            case ActionType.ResetMovePlanOperationAction:
                storeChanged = this.handleResetMovePlanOperationAction(action);
                break;
            case ActionType.SetPlanFilterAction:
                storeChanged = this.handleSetPlanFilterAction(action);
                break;
            case ActionType.SetRecommendedPlansAction:
                storeChanged = this.setSpecialPlanIds(RecommendedPlanSetId, action.planIds);
                break;
            case ActionType.RemovePlanFromRecommendedListAction:
                storeChanged = this.setSpecialPlanIds(RecommendedPlanSetId, this.getRecommendedPlanIds().filter((id)=>id !== action.planId));
                this.planTable.refreshIndex("view");
                break;
            case ActionType.DeltaSyncAction:
                storeChanged = this.planSlice.handleDeltaSyncStarted({
                    name: "containerId",
                    value: toContainerIdString(action.parentType, action.parentId)
                });
                break;
            case ActionType.DeltaSyncSucceededAction:
                storeChanged = this.planSlice.handleDeltaSyncSucceeded({
                    name: "containerId",
                    value: toContainerIdString(action.parentType, action.parentId)
                }, Object.fromEntries(action.deltas.map((item)=>[
                        item.entity.id,
                        item
                    ])), action.deltaLink);
                break;
            case ActionType.DeltaSyncFailedAction:
                storeChanged = this.planSlice.handleDeltaSyncFailed({
                    name: "containerId",
                    value: toContainerIdString(action.parentType, action.parentId)
                }, action.response);
                break;
            case ActionType.CreatePlanFromTemplateAction:
                storeChanged = this.handleCreatePlanFromTemplateAction(action);
                break;
            case ActionType.CreatePlanFromTemplateSucceededAction:
                storeChanged = this.handleCreatePlanFromTemplateSucceededAction(action);
                break;
            case ActionType.CreatePlanFromTemplateFailedAction:
                storeChanged = this.handleCreatePlanFromTemplateFailedAction(action);
                break;
            case ActionType.CreatePlanFromTemplateResetAction:
                storeChanged = this.handleCreatePlanFromTemplateResetAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleMovePlanOperationAction(action) {
        return this.movePlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.InProgress);
    }
    handleMovePlanOperationCompletedAction(action) {
        return this.movePlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.Completed);
    }
    handleMovePlanOperationCompletedWithErrorsAction(action) {
        return this.movePlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.CompletedWithErrors);
    }
    handleResetMovePlanOperationAction(action) {
        return this.movePlanOperationStatusTable.set(action.planId, null);
    }
    /**
     * Handles the create plan from template action and sets the operation status to running
     * @param action The create plan from template action
     */ handleCreatePlanFromTemplateAction(action) {
        return this.createPlanFromTemplateOperationStatusTable.set(action.planId, OperationStatus.Running);
    }
    /**
     * Handles the create plan from template succeeded action and sets the operation status to completed successfully
     * @param action The create plan from template succeeded action
     */ handleCreatePlanFromTemplateSucceededAction(action) {
        return this.createPlanFromTemplateOperationStatusTable.set(action.planId, OperationStatus.CompletedSuccessfully);
    }
    /**
     * Handles the create plan from template failed action and sets the operation status to completed with errors
     * @param action The create plan from template failed action
     */ handleCreatePlanFromTemplateFailedAction(action) {
        return this.createPlanFromTemplateOperationStatusTable.set(action.planId, OperationStatus.CompletedWithErrors);
    }
    /**
     * Handles the create plan from template reset action and resets the operation status
     * @param action The create plan from template reset action
     */ handleCreatePlanFromTemplateResetAction(action) {
        return this.createPlanFromTemplateOperationStatusTable.set(action.planId, null);
    }
    /**
     * Handle application of plan filter on the view
     */ handleSetPlanFilterAction(action) {
        const keyword = `${action.filter?.filters?.find((filter)=>{
            return filter.field === "name";
        })?.values?.[0] ?? ""}`.toLocaleLowerCase();
        applyFilter(this.getMyPlansViewPartition(), (plan)=>keyword.length > 0 ? plan.displayName.toLocaleLowerCase().indexOf(keyword) !== -1 : true);
        return true;
    }
    /**
     * Helper to set a special plan in store
     */ setSpecialPlanIds(key, planIds) {
        const storeChanged = this.specialPlansTable.set(key, planIds);
        // Refresh the applied filter for specified pivots
        if (storeChanged) {
            // Pivot membership can change in response to updates to known plan id's, hence index needs to be refreshed
            this.getMyPlansViewPartition().refreshIndex(MyPlansViewPivotIndex);
        }
        return storeChanged;
    }
    // Helper to get MyPlans view pivots for a plan
    getMyPlansViewPivotsForPlan(plan) {
        const pivots = [];
        // Case 1: Known plans do not show up in any pivot
        if (plan.id === this.getDefaultPlanId() || plan.id === this.getFlaggedEmailPlanId()) {
            return pivots;
        }
        // Case 2: If a plan is in user container or is a personal (no sharing link) todo list, it should show up in personal pivot, shared otherwise
        if (plan.container.containerType === ContainerType.User || plan.container.containerType === ContainerType.ToDo && !plan.extensions.todo?.sharingLink) {
            pivots.push(MyPlansPagePivotType.Personal);
        } else {
            pivots.push(MyPlansPagePivotType.Shared);
        }
        // Case 3: If plan is in the recommended set, show in the recommended pivot
        if (this.getRecommendedPlanIds().includes(plan.id)) {
            pivots.push(MyPlansPagePivotType.Recommended);
        }
        // Case 4: All plans should show up in recent pivot (this is esp. needed once we fetch all user accessible plans)
        pivots.push(MyPlansPagePivotType.Recent);
        return pivots;
    }
    // Helper to get the partition for MyPlans view
    getMyPlansViewPartition() {
        return this.planTable.index("view").partition(MyPlansViewPartition);
    }
    shouldHandleAction(action) {
        if (isEntityAction(action)) {
            // Only handle entity actions for plans
            return action.entityType === EntityType.Plan;
        }
        return true; // Handle all other actions
    }
    // Helper to get the plan table from the slice
    get planTable() {
        return this.planSlice.table();
    }
    constructor(loggers, configProvider){
        super(), this.loggers = loggers, this.configProvider = configProvider, /**
     * Plan table partition structure
     *
     * ```
     * ☶ Root Table: All plans
     * ├── ContainerType
     * │   ├── ☶ User
     * │   ├── ☶ Group
     * │   └── ☶ DriveItem
     * │
     * ├── ContainerId
     * │   ├── ☶ Group:G1
     * │   ├── ☶ Group:G1
     * │   ├── ☶ User:U1
     * │   ├── ☶ DriveItem:D1
     * │   ├── ☶ Roster:R1
     * │   └── ☶ Roster:R2
     * │
     * └── View
     *     └── ☶ MyPlansView
     *         └── Pivot
     *             ├── ☶ Personal
     *             ├── ☶ Shared
     *             └── ☶ Recommended
     * ```
     */ this.planSlice = new EntitySlice(TableName.Plan, {
            /** An index with a single partition per container type  */ ["containerType"]: (plan)=>plan.container.containerType,
            /** An index with a single partition per container id  */ ["containerId"]: (plan)=>toContainerIdString(plan.container.containerType, plan.container.containerId),
            /** An index for serving view queries (currently only MyPlansView)  */ ["view"]: (plan)=>MyPlansViewPartition
        }, this.configProvider), // Special plans
        this.specialPlansTable = new Table("specialPlans", (a, b)=>{
            // For arrays with single value, compare the value
            if (a.length === 1 && b.length === 1) {
                return a[0] === b[0];
            }
            // For multiple values, always assume they are different for simplicity
            return false;
        }), this.movePlanOperationStatusTable = new Table(StoreTableNames.MoveBasicPlanOperationStatus), this.createPlanFromTemplateOperationStatusTable = new Table(StoreTableNames.CreatePlanFromTemplateOperationStatus);
        // Register an index on the MyPlansView partition for view pivots so that we have a partition for each pivot
        registerIndex(this.getMyPlansViewPartition(), MyPlansViewPivotIndex, (plan)=>this.getMyPlansViewPivotsForPlan(plan));
    }
}
/** Helper to convert container type and ID to a string */ function toContainerIdString(containerType, containerId) {
    return `${containerType}-${containerId}`;
}
